import { Game } from '../data-types/casino/casinoInterfaces';
import { AvailableLanguage } from '../data-types/international/internationalEnums';
import { sportCategories } from '../data-types/sports/sportsConstants';
import { EventItem, Team } from '../data-types/sports/sportsInterfaces';
import { DEFAULT_RETRY_INTERVAL } from '../data-types/timing/timingConstants';
import { CURRENCY_SYMBOL } from '../data-types/wallet/walletEnums';
import { SPORTRADAR_PIXEL_ENABLED } from '../initialisation/initialisation';
import { getCurrencyNameBySymbol } from '../international/internationalCurrencies';
import { getCasinoCategoryNameById } from '../utils/casino';

enum TRACK {
  REGISTRATION = 'track.user.registration',
  LOGIN = 'track.user.login',
  DEPOSIT = 'track.betting.deposit',
  BETSLIP = 'srt.betslip',
  BET_PLACED = 'track.betting.betPlaced',
  RETARGETING = 'srt.retargeting',
}

enum ACTION {
  COMPLETE = 'complete',
  START = 'start',
  CREATED = 'created',
}

enum LABEL {
  FIRST_DEPOSIT = 'first_deposit',
}

enum SPORT_NAME {
  FOOTBALL = 'soccer',
}

const getTeamShortName = (team?: Team) => (team && team.name && team.name !== 'N/A' ? team.name : '');

const waitUntilSrtmIsDefined = (callback: () => void) => {
  const interval = setInterval(() => {
    if (window.srtmCommands) {
      callback();
      clearInterval(interval);
    }
  }, DEFAULT_RETRY_INTERVAL);
};

export const trackSportradarStartRegistration = () => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(registrationStarted);
  }
};

export const trackSportradarFinishRegistration = (userId: number) => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(registrationFinished(userId));
  }
};

export const trackSportradarLogin = (userId: number) => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(loggedIn(userId));
  }
};

export const trackSportradarDeposit = (userId: number, amount: number, currency: string, isFirstDeposit: boolean) => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(deposit(userId, amount, currency, isFirstDeposit));
  }
};

export const trackSportradarLaunchCasinoGame = (game: Game, casinoCategory?: number) => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(launchCasinoGame(game, casinoCategory));
  }
};

export const trackSportradarPlaceBet = (userId: number, amount: number, currency: string, eventIds: string[]) => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(placeBet(userId, amount, currency, eventIds));
  }
};

export const trackSportradarAddToBeslip = (event: EventItem, locale: AvailableLanguage | undefined) => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(addToBetslip(event, locale));
  }
};

export const trackSportradarOpenEventDetails = (event: EventItem, locale: AvailableLanguage | undefined) => {
  if (SPORTRADAR_PIXEL_ENABLED) {
    waitUntilSrtmIsDefined(openEventDetails(event, locale));
  }
};

const registrationStarted = () => {
  window.srtmCommands.push({
    event: TRACK.REGISTRATION,
    payload: {
      action: ACTION.START,
    },
  });
};

const registrationFinished = (userId: number) => () => {
  window.srtmCommands.push({
    event: TRACK.REGISTRATION,
    payload: {
      action: ACTION.COMPLETE,
      userId: String(userId),
    },
  });
};

const loggedIn = (userId: number) => () => {
  window.srtmCommands.push({
    event: TRACK.LOGIN,
    payload: {
      action: ACTION.COMPLETE,
      userId: String(userId),
    },
  });
};

const deposit = (userId: number, amount: number, currency: string, firstDeposit: boolean) => () => {
  window.srtmCommands.push({
    event: TRACK.DEPOSIT,
    payload: {
      currency: getCurrencyNameBySymbol(currency as CURRENCY_SYMBOL),
      action: ACTION.CREATED,
      transactionId: '',
      userId: String(userId),
      amount: String(amount),
      label: firstDeposit ? LABEL.FIRST_DEPOSIT : undefined,
    },
  });
};

const launchCasinoGame = (game: Game, casinoCategory?: number) => () => {
  const categoryName = typeof casinoCategory === 'number' && getCasinoCategoryNameById(casinoCategory);

  if (categoryName) {
    window.srtmCommands.push({
      event: TRACK.BETSLIP,
      payload: {
        casinoEvent: {
          game: {
            id: `${game.gameId}`,
            name: `${game.name}`,
            category: categoryName,
            image: game.imageURL ? `${game.imageURL}${game.gameId}-SQ.png` : '',
            provider: {
              id: `${game.providerId || ''}`,
              game_id: `${game.pid}`,
            },
          },
          event: {
            type: 'page_view',
          },
          version: '1-0-0',
        },
      },
    });
  }
};

const placeBet = (userId: number, amount: number, currency: string, eventIds: string[]) => () => {
  window.srtmCommands.push({
    event: TRACK.BET_PLACED,
    payload: {
      userId: `${userId}`,
      transactionId: '',
      amount: `${amount}`,
      currency: `${currency}`,
      contentIds: eventIds,
    },
  });
};

const getMainOfferFromEvent = (event: EventItem) =>
  event.details && event.details.offers.find(offerItem => offerItem.name === 'Full Time Result' || offerItem.name === 'Match Winner');

const addToBetslip = (event: EventItem, locale: AvailableLanguage | undefined) => () => {
  const sportName = event.sportId === sportCategories.FOOTBALL ? SPORT_NAME.FOOTBALL : '';
  const offer = getMainOfferFromEvent(event);
  if (sportName) {
    window.srtmCommands.push({
      event: TRACK.BETSLIP,
      payload: {
        sportsEvent: {
          match_id: `${event.id}`,
          sr_match_id: event.matchId ? `${event.matchId}` : '',
          team_home_name: `${(event.homeTeam && event.homeTeam.name) || ''}`,
          team_home_name_short: getTeamShortName(event.homeTeam),
          team_home_logo: '',
          team_away_name: `${(event.awayTeam && event.awayTeam.name) || ''}`,
          team_away_name_short: getTeamShortName(event.awayTeam),
          team_away_logo: '',
          scheduled: `${event.startDate}`,
          status: '',
          tournament_name: `${event.competition.name}`,
          sport: sportName,
          odds_home: offer && offer.outcomes[0] ? `${offer.outcomes[0].odds}` : '',
          odds_draw: offer && offer.outcomes[1] ? `${offer.outcomes[1].odds}` : '',
          odds_away: offer && offer.outcomes[2] ? `${offer.outcomes[2].odds}` : '',
          language: locale || '',
        },
      },
    });
  }
};

const openEventDetails = (event: EventItem, locale: AvailableLanguage | undefined) => () => {
  const sportName = event.sportId === sportCategories.FOOTBALL ? SPORT_NAME.FOOTBALL : '';
  const offer = getMainOfferFromEvent(event);

  if (sportName) {
    window.srtmCommands.push({
      event: TRACK.RETARGETING,
      payload: {
        sportsEvent: {
          match_id: `${event.id}`,
          sr_match_id: event.matchId ? `${event.matchId}` : '',
          team_home_name: `${(event.homeTeam && event.homeTeam.name) || ''}`,
          team_home_name_short: getTeamShortName(event.homeTeam),
          team_home_logo: '',
          team_away_name: `${(event.awayTeam && event.awayTeam.name) || ''}`,
          team_away_name_short: getTeamShortName(event.awayTeam),
          team_away_logo: '',
          scheduled: `${event.startDate}`,
          status: '',
          tournament_name: `${event.competition.name}`,
          sport: sportName,
          odds_home: offer && offer.outcomes[0] ? `${offer.outcomes[0].odds}` : '',
          odds_draw: offer && offer.outcomes[1] ? `${offer.outcomes[1].odds}` : '',
          odds_away: offer && offer.outcomes[2] ? `${offer.outcomes[2].odds}` : '',
          language: locale || '',
        },
      },
    });
  }
};
